<script>
import { ref, computed } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useTrack } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    EditorModeToggle,
    CopilotMenuBar,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
    hasContent: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['setReplyMode', 'toggleEditorSize', 'executeCopilotAction'],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleModeToggle = () => {
      const newMode =
        props.mode === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(newMode);
    };

    const { captainTasksEnabled } = useCaptain();

    // 客服强制介入:介入中 AI 不回复,由客服手动答。标识 = conversation.custom_attributes.agent_takeover
    const store = useStore();
    const currentChat = useMapGetter('getSelectedChat');
    const agentTakeoverLoading = ref(false);
    const isAgentTakeover = computed(() => {
      const v = currentChat.value?.custom_attributes?.agent_takeover;
      return String(v).toLowerCase() === 'true';
    });
    const toggleAgentTakeover = async () => {
      const conv = currentChat.value;
      if (!conv?.id || agentTakeoverLoading.value) return;
      // 后端 custom_attributes 是整体替换,先合并现有值再提交,避免清掉别的字段
      const existing = conv.custom_attributes || {};
      agentTakeoverLoading.value = true;
      try {
        await store.dispatch('updateCustomAttributes', {
          conversationId: conv.id,
          customAttributes: {
            ...existing,
            agent_takeover: String(!isAgentTakeover.value),
          },
        });
      } finally {
        agentTakeoverLoading.value = false;
      }
    };
    const showCopilotMenu = ref(false);
    const copilotToggleRef = ref(null);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
          conversationId: props.conversationId,
          entryPoint: 'top_panel',
        });
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: false,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: false,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
      captainTasksEnabled,
      handleCopilotAction,
      showCopilotMenu,
      copilotToggleRef,
      toggleCopilotMenu,
      handleClickOutside,
      isAgentTakeover,
      agentTakeoverLoading,
      toggleAgentTakeover,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
};
</script>

<template>
  <div
    class="flex justify-between gap-2 h-[3.25rem] items-center ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2"
  >
    <EditorModeToggle
      :mode="mode"
      :disabled="disabled"
      :is-reply-restricted="isReplyRestricted"
      @toggle-mode="handleModeToggle"
    />
    <div class="flex items-center mx-4 my-0">
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <div class="flex items-center gap-2">
      <button
        type="button"
        :disabled="agentTakeoverLoading"
        class="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-60"
        :class="
          isAgentTakeover
            ? 'bg-red-500 text-white'
            : 'bg-n-brand text-white'
        "
        @click="toggleAgentTakeover"
      >
        <span
          :class="isAgentTakeover ? 'i-lucide-bot' : 'i-lucide-hand'"
          class="flex-shrink-0 size-4"
        ></span>
        {{ isAgentTakeover ? '已介入·AI暂停' : '强制介入' }}
      </button>
      <template v-if="captainTasksEnabled">
        <div class="relative">
          <NextButton
            ref="copilotToggleRef"
            ghost
            :disabled="disabled || isEditorDisabled"
            :class="{
              'text-n-violet-9 hover:enabled:!bg-n-violet-3': !showCopilotMenu,
              'text-n-violet-9 bg-n-violet-3': showCopilotMenu,
            }"
            sm
            icon="i-ph-sparkle-fill"
            @click="toggleCopilotMenu"
          />
          <CopilotMenuBar
            v-if="showCopilotMenu"
            v-on-click-outside="[
              handleClickOutside,
              { ignore: [copilotToggleRef] },
            ]"
            :has-selection="false"
            :has-content="hasContent"
            :conversation-id="conversationId"
            class="ltr:right-0 rtl:left-0 bottom-full mb-2"
            @execute-copilot-action="handleCopilotAction"
          />
        </div>
        <NextButton
          ghost
          class="text-n-slate-11"
          sm
          icon="i-lucide-maximize-2"
          @click="$emit('toggleEditorSize')"
        />
      </template>
    </div>
  </div>
</template>
